import React, { type ComponentType, useCallback, useEffect, useState } from 'react';
import { isFedRamp } from '@atlassian/atl-context';
import { AsyncJiraCommandPalette } from '@atlassian/jira-command-palette-integration/src/async';
import componentWithConditionOld from '@atlassian/jira-common-util-component-with-condition';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { FavouriteChangeImperativeApi } from '@atlassian/jira-favourite-change-provider';
import { ff } from '@atlassian/jira-feature-flagging';
import { componentWithFF } from '@atlassian/jira-feature-flagging-utils';
import { FeedbackCollectorRenderer } from '@atlassian/jira-feedback-collector';
import LoadGICResources from '@atlassian/jira-global-issue-create-resources';
import InvitePeopleDrawer from '@atlassian/jira-invite-people-drawer';
import AsyncIssueArchivalModal from '@atlassian/jira-issue-archival-modal';
import { AsyncIssueCreateModal } from '@atlassian/jira-issue-create';
import {
	AsyncNinChangeboardingAdminModal,
	fetchHasSeenNinChangeboardingAdminModal,
} from '@atlassian/jira-issue-navigator-changeboarding';
import { AsyncIssueTransitionModal } from '@atlassian/jira-issue-transition';
import LegacyAsyncModals from '@atlassian/jira-legacy-async-modals';
import AsyncNewIssueCreateTrigger from '@atlassian/jira-new-issue-create-trigger';
import CreateNewPlanWizard from '@atlassian/jira-portfolio-3-create-new-plan-wizard-drawer';
import CreateSamplePlan from '@atlassian/jira-portfolio-3-create-sample-plan';
import ProjectCreateDrawer from '@atlassian/jira-project-create-drawer';
import { useResource } from '@atlassian/jira-router';
import { gicOptInStatusResources } from '@atlassian/jira-router-resources-global-issue-create-switch';
import ShortcutsDialog from '@atlassian/jira-shortcuts-dialog';
import { FlagIssueWithCommentModal } from '@atlassian/jira-software-add-issue-flag-with-comment';
import { AsyncEnforceStorageLimitsModal } from '@atlassian/jira-software-enforce-storage-limits/src/ui/modal/async';
import { useAppEditions, useIsAdmin, useLocale } from '@atlassian/jira-tenant-context-controller';
import { COMMAND_PALETTE_CONTAINER_TEST_ID } from './constants';

const useIsModernGlobalIssueCreatePreferred = () =>
	useResource(gicOptInStatusResources).data?.gicOptInStatus?.isEnabled ?? false;

const LegacyComponents = () => {
	const baseUrl = '';
	const locale = useLocale();

	return (
		<>
			<LegacyAsyncModals baseUrl={baseUrl} locale={locale} />
		</>
	);
};

const ProjectCreateDrawerComponent = () => {
	const appEditions = useAppEditions();
	const isAdmin = useIsAdmin();
	const baseUrl = '';
	const locale = useLocale();

	return (
		<ProjectCreateDrawer
			baseUrl={baseUrl}
			isAdmin={isAdmin}
			isOnboarding={false}
			locale={locale}
			appEditions={appEditions}
		/>
	);
};

const ModernGlobalIssueCreate = componentWithConditionOld(
	() =>
		ff('project.config.pce.modern.global-issue-create') && useIsModernGlobalIssueCreatePreferred(),
	() => (
		<>
			<AsyncEnforceStorageLimitsModal />
			<AsyncIssueCreateModal />
			{ff('project.config.pce.modern.global-issue-create.trigger') ? (
				<AsyncNewIssueCreateTrigger />
			) : null}
		</>
	),
	() => null,
);
const IssueArchiveModal = componentWithFF('issue-unarchival_wdpb9', AsyncIssueArchivalModal, () => (
	<></>
));

const ModernIssueTransition = componentWithConditionOld(
	() => ff('render-modern-issue-transition_cxt0z') || isFedRamp(),
	AsyncIssueTransitionModal,
	() => <></>,
);

const NinChangeboardingAdminModal = () => {
	const isAdmin = useIsAdmin();
	const [userFirstModalVisit, setUserFirstModalVisit] = useState(false);

	const fireErrorAnalyticWhenFetchHasSeenNinChangeboardingAdminModalUnsuccessful = useCallback(
		(cause: Error) => {
			const err = new Error('Fetch has seen NIN changeboarding admin modal was not successful', {
				cause,
			});
			fireErrorAnalytics({
				meta: {
					id: 'adminModal',
					packageName: 'jiraSharedLayout',
					teamName: 'magma',
				},
				sendToPrivacyUnsafeSplunk: true,
				error: err,
			});
		},
		[],
	);

	useEffect(() => {
		async function checkUserFirstModalVisit() {
			try {
				const hasSeenNinChangeboardingAdminModal = await fetchHasSeenNinChangeboardingAdminModal();
				setUserFirstModalVisit(!hasSeenNinChangeboardingAdminModal);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				fireErrorAnalyticWhenFetchHasSeenNinChangeboardingAdminModalUnsuccessful(err);
			}
		}
		isAdmin === true && checkUserFirstModalVisit();
	}, [
		fireErrorAnalyticWhenFetchHasSeenNinChangeboardingAdminModalUnsuccessful,
		setUserFirstModalVisit,
		isAdmin,
	]);
	return isAdmin === true && userFirstModalVisit ? <AsyncNinChangeboardingAdminModal /> : null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Global: ComponentType<Record<any, any>> = () => (
	<>
		<FavouriteChangeImperativeApi />
		<LoadGICResources />
		<FeedbackCollectorRenderer />
		<LegacyComponents />
		<CreateNewPlanWizard />
		<CreateSamplePlan />
		<InvitePeopleDrawer />
		<ProjectCreateDrawerComponent />
		<IssueArchiveModal />
		<ModernGlobalIssueCreate />
		<ModernIssueTransition />
		<NinChangeboardingAdminModal />
		<ShortcutsDialog />
		<div data-testid={COMMAND_PALETTE_CONTAINER_TEST_ID}>
			<AsyncJiraCommandPalette />
		</div>
		<FlagIssueWithCommentModal />
	</>
);

export { NinChangeboardingAdminModal };
export default Global;
